import { GenData } from './gen-data';
import { QSingle, QMulti } from './gen';
import { IGenEq, IGenSingle, IGenMulti } from './gen-interface';

/**
 * Wraps an IGenEq as an IGenSingle.
 * Equivalent to C# SingleRegister.EqSingleGen adapter.
 *
 * QEq { text, result } → QSingle { texts: [text], answer: result.toString() }
 */
export function eqToSingle(eq: IGenEq): IGenSingle {
  return {
    generate(data: GenData): QSingle {
      const r = eq.generate(data);
      return { texts: [r.text], answer: String(r.result) };
    },
  };
}

/**
 * Wraps an IGenSingle as an IGenMulti by generating wrong answers from the same generator.
 *
 * Generates one correct answer plus (choiceCount-1) distractors.
 */
export function singleToMulti(single: IGenSingle, choiceCount = 3): IGenMulti {
  return {
    generate(data: GenData): QMulti {
      const correct = single.generate(data);
      const answers: string[] = [correct.answer];

      // Generate distractors from the same generator
      let tries = 0;
      while (answers.length < choiceCount) {
        const distractor = single.generate(data);
        if (!answers.includes(distractor.answer)) {
          answers.push(distractor.answer);
        }
        if (tries++ > 1000) {
          // Fallback: just add modified answers
          answers.push(`${correct.answer  }?`);
          break;
        }
      }

      // Shuffle answers, track correct index
      const shuffled = [...answers];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = data.random.next(0, i + 1);
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }

      return {
        texts: correct.texts,
        answers: shuffled,
        answerIndex: shuffled.indexOf(correct.answer),
      };
    },
  };
}

/**
 * Shortcut: IGenEq → IGenMulti (eq → single → multi).
 */
export function eqToMulti(eq: IGenEq, choiceCount = 3): IGenMulti {
  return singleToMulti(eqToSingle(eq), choiceCount);
}

/**
 * Creates an IGenMulti from IGenEq where distractors are nearest integers to the correct answer.
 * E.g. if answer is 7 and choiceCount is 3, answers could be [6, 7, 8].
 */
export function nearestMulti(eq: IGenEq, choiceCount = 3): IGenMulti {
  return {
    generate(data: GenData): QMulti {
      const r = eq.generate(data);
      const correct = r.result;

      if (typeof correct !== 'number') {
        // Fallback for non-numeric results
        const answers = [String(correct)];
        while (answers.length < choiceCount) {
          answers.push(String(correct) + (answers.length + 1));
        }
        return { texts: [r.text], answers, answerIndex: 0 };
      }

      const answers: number[] = [correct];
      // Add nearest integers: ±1, ±2, etc.
      let offset = 1;
      while (answers.length < choiceCount) {
        if (answers.length < choiceCount) {
          const candidate = correct + offset;
          if (candidate >= 0 && !answers.includes(candidate)) {
            answers.push(candidate);
          }
        }
        if (answers.length < choiceCount) {
          const candidate = correct - offset;
          if (candidate >= 0 && !answers.includes(candidate)) {
            answers.push(candidate);
          }
        }
        offset++;
        if (offset > 100) break;
      }

      // Sort ascending
      answers.sort((a, b) => a - b);
      const strAnswers = answers.map(String);

      return {
        texts: [r.text],
        answers: strAnswers,
        answerIndex: strAnswers.indexOf(String(correct)),
      };
    },
  };
}

/**
 * Creates an IGenMulti from IGenEq where distractors are random integers near the correct answer.
 * Picks random values within ±choiceCount of the correct result, ensuring uniqueness.
 */
export function mixedMulti(eq: IGenEq, choiceCount = 3): IGenMulti {
  return {
    generate(data: GenData): QMulti {
      const r = eq.generate(data);
      const correct = r.result;

      if (typeof correct !== 'number') {
        // Fallback for non-numeric results
        const answers = [String(correct)];
        while (answers.length < choiceCount) {
          answers.push(String(correct) + (answers.length + 1));
        }
        return { texts: [r.text], answers, answerIndex: 0 };
      }

      const answers: number[] = [correct];
      let tries = 0;
      while (answers.length < choiceCount && tries < 1000) {
        const offset =
          data.random.next(1, choiceCount + 1) * (data.random.next(0, 2) === 0 ? -1 : 1);
        const candidate = correct + offset;
        if (candidate >= 0 && !answers.includes(candidate)) {
          answers.push(candidate);
        }
        tries++;
      }

      // Shuffle
      for (let i = answers.length - 1; i > 0; i--) {
        const j = data.random.next(0, i + 1);
        [answers[i], answers[j]] = [answers[j], answers[i]];
      }

      const strAnswers = answers.map(String);
      return {
        texts: [r.text],
        answers: strAnswers,
        answerIndex: strAnswers.indexOf(String(correct)),
      };
    },
  };
}
